import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { formatSchedule } from '../utils/schedule';

/**
 * "Are you sure?" before changing the delivery days of a product or plan that customers are on.
 *
 * Days are not dated: a change reaches every customer on it, and any month whose bill is made
 * again (from the 1st). Closed months keep their bills. Shared by the product and plan forms.
 *
 * props:
 *   open
 *   schedule       the new days, { f_schedule_type, f_schedule_value }
 *   customerCount  customers on the product/plan now
 *   kind           'product' | 'plan' — used in the sentence
 *   onCancel / onConfirm
 */
export const daysChanged = (before, after) => {
    const shape = (s) => {
        const type = s?.f_schedule_type || 'daily';
        // "Every day" has many spellings (daily, every 1 day, all 7 weekdays); compare by meaning.
        if (formatSchedule(s) === 'Every day') return 'daily';
        return `${type}:${Number(s?.f_schedule_value ?? 1)}:${type === 'interval' ? (s?.f_anchor_date || '') : ''}`;
    };
    return shape(before) !== shape(after);
};

const ScheduleChangeConfirm = ({ open, schedule, customerCount, kind = 'product', onCancel, onConfirm }) => {
    const month = new Date().toLocaleDateString('en-IN', { month: 'long' });
    const n = customerCount || 0;
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
                Change delivery days to {formatSchedule(schedule)}?
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <b>{n} {n === 1 ? 'customer is' : 'customers are'}</b> on this {kind}. The new days apply
                    to all of them.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.25 }}>
                    {month}'s bill, if it is still open, changes from the 1st when bills are made again.
                    Closed months don't change.
                </Typography>
                <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 1.5, px: 1.25, py: 1 }}>
                    <Typography variant="caption" sx={{ color: '#795548' }}>
                        Tip: change days at the start of a month to avoid surprises.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button variant="outlined" onClick={onCancel} sx={{ minHeight: 44, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={onConfirm} disableElevation
                    sx={{ flex: 1, minHeight: 44, textTransform: 'none', fontWeight: 700 }}>
                    Yes, change days
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleChangeConfirm;
